/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  IS_REGION_AU_OR_NZ,
  isEntityInvalid,
  markFormStateAsTouched,
  PlainImmutablePageState,
  queryProduceStatus,
  queryRecalculationNeeded,
  setEntityFields,
} from '@gh/common-shared';
import { ID, isProduceStatusInProgress, ProduceStatus } from '@gh/common-shared-data';
import { isSelectionModelEmpty, SelectionModel } from '@gh/core-data';
import { FormMode } from '@gh/core-ui';
import { differentFieldsWith, isNotNil } from '@gh/core-util';
import { distinctUntilChanged$, map$, switchMap$ } from '@gh/rx/operators';

import { find, isEmpty, isNil, negate } from 'lodash';

import {
  DRAW_DETAILS_INIT,
  DrawCalculateResponse,
  DrawDetails,
  DrawDetailsCurrentInvoice,
  DrawDetailsLendersInfo,
  DrawDetailsLendersScheduleItem,
  DrawDetailsMasterInfo,
  DrawDetailsPaymentInfo,
  DrawDetailsPaymentProgressInfo,
  DrawDetailsPaymentTab,
  drawLendersScheduleItemArr,
  getDrawLastStageNumber,
  getDrawLendersScheduleItemId,
  mergeDrawDetailsAmountWithGst,
  queryDrawDetailsIsLendersSchedule,
  selectDrawRuleStage,
  setDrawLendersScheduleItemField,
  validateDrawLendersInfo,
  validateDrawPaymentInfo,
  validateDrawPaymentProgressInfo,
} from '../../resources/draw-details.model';

@Injectable()
export class DrawDetailsFormService extends PlainImmutablePageState<DrawDetails> {
  recalculationNeeded$ = this.select(queryRecalculationNeeded);
  job$ = this.select(({ job }) => job);
  paymentInfo$ = this.select(({ paymentInfo }) => paymentInfo);
  lendersInfo$ = this.select(({ lendersInfo }) => lendersInfo);
  currentStage$ = this.paymentInfo$.pipe(map$(getFormStateSnapshotValue), map$(({ drawRuleStage }) => drawRuleStage));
  lastStageNumber$ = this.select(({ stages }) => stages).pipe(
    switchMap$(() => this.select(getDrawLastStageNumber)));
  paymentProgressInfo$ = this.select(({ paymentProgressInfo }) => paymentProgressInfo);
  paymentScheduleForStage$ = this.currentStage$.pipe(switchMap$((stage) =>
    this.select(({ paymentSchedule }) => paymentSchedule.filter(({ stageNumber }) => stageNumber <= stage))));
  stages$ = this.select(({ stages }) => stages);
  paymentSchedule$ = this.select(({ paymentSchedule }) => paymentSchedule);
  lendersSchedule$ = this.select(({ lendersSchedule }) => lendersSchedule);
  visibleLendersSchedule$ = this.lendersInfo$.pipe(
    map$(getFormStateSnapshotValue),
    switchMap$(({ isShowAllCostCenters }) =>
      isShowAllCostCenters
        ? this.select(({ lendersSchedule }) => lendersSchedule)
        : this.select(({ lendersSchedule }) => lendersSchedule.filter(({ balance }) => balance > 0))));
  changeOrders$ = this.select(({ changeOrders }) => changeOrders);
  isJobSelected$ = this.job$.pipe(map$(isNotNil));
  isLendersSchedule$ = this.select(queryDrawDetailsIsLendersSchedule);
  drawRuleId$ = this.paymentInfo$.pipe(map$(getFormStateSnapshotValue), map$(({ drawRuleId }) => drawRuleId));
  drawRuleCode$ = this.paymentInfo$.pipe(map$(getFormStateSnapshotValue), map$(({ drawRuleCode }) => drawRuleCode));
  drawRuleName$ = this.paymentInfo$.pipe(map$(getFormStateSnapshotValue), map$(({ drawRuleName }) => drawRuleName));
  selectedTab$ = this.select(({ selectedTab }) => selectedTab);
  selectedChangeOrders$ = this.select(({ selectedChangeOrders }) => selectedChangeOrders);
  hasSelectedChangeOrders$ = this.selectedChangeOrders$.pipe(map$(negate(isSelectionModelEmpty)));
  lendersProgressDrawn$ = this.lendersInfo$.pipe(
    map$(getFormStateSnapshotValue),
    map$(({ contractDrawnAmount }) => contractDrawnAmount));
  lendersScheduleFocusedCell$ = this.select(({ lendersScheduleFocusedCell }) => lendersScheduleFocusedCell);
  invoices$ = this.select(({ invoices }) => invoices);
  hasInvoice$ = this.invoices$.pipe(map$(negate(isEmpty)));
  produceStatus$ = this.select(queryProduceStatus);
  producing$ = this.produceStatus$.pipe(map$(isProduceStatusInProgress), distinctUntilChanged$());
  isPreview$ = this.select(({ isPreview }) => isPreview);
  isQuoteVariationExist$ = this.select(({ isQuoteVariationExist }) => isQuoteVariationExist);

  isCreateMode: boolean;

  get jobId(): ID {
    const { job } = this.value;
    return job && job.jobId;
  }

  get job(): DrawDetailsMasterInfo {
    return this.value.job;
  }

  get thisDraw(): number {
    return getFormStateSnapshotValue(this.value.paymentProgressInfo).thisDraw;
  }

  get invalid(): boolean {
    return this.isLendersSchedule
      ? this.isLendersInfoInvalid || this.isLendersScheduleInvalid
      : this.isRequiredTabNotVisited || this.isPaymentInfoInvalid || this.isPaymentProgressInfoInvalid;
  }

  get isLendersSchedule(): boolean {
    return this.project(queryDrawDetailsIsLendersSchedule);
  }

  get isRequiredTabNotVisited(): boolean {
    const { changeOrders, tabs: { isChangeOrdersVisited, isProgressVisited } } = this.value;
    return changeOrders.length > 0 && !isChangeOrdersVisited || !isProgressVisited;
  }

  get isPaymentInfoInvalid(): boolean {
    return isNotNil(validateDrawPaymentInfo(getFormStateSnapshotValue(this.value.paymentInfo)));
  }

  get isPaymentProgressInfoInvalid(): boolean {
    const hasSelectedChangeOrders = !isSelectionModelEmpty(this.value.selectedChangeOrders);
    return isNotNil(validateDrawPaymentProgressInfo({
      ...getFormStateSnapshotValue(this.value.paymentProgressInfo),
      hasSelectedChangeOrders,
    }));
  }

  get isLendersInfoInvalid(): boolean {
    return isNotNil(validateDrawLendersInfo(getFormStateSnapshotValue(this.value.lendersInfo)));
  }

  get isLendersScheduleInvalid(): boolean {
    return this.value.lendersSchedule.some(isEntityInvalid);
  }

  get currentInvoice(): DrawDetailsCurrentInvoice {
    return this.value.currentInvoice!; // tslint:disable-line:no-non-null-assertion
  }

  get currentInvoiceId(): Maybe<ID> {
    return this.value.currentInvoiceId;
  }

  get isPreview(): boolean {
    return this.value.isPreview;
  }

  get isProgressInvoice(): boolean {
    return isNil(this.currentInvoice.variationId);
  }

  get isChangeOrderInvoice(): boolean {
    return isNotNil(this.currentInvoice.variationId);
  }

  constructor() {
    super(DRAW_DETAILS_INIT);
  }

  build(details: DrawDetails, isCreateMode: boolean): void {
    this.isCreateMode = isCreateMode;
    this.setValue(details);
    this.updateMode();
    this.resetDirty();
  }

  setProducingStatus(producingStatus: ProduceStatus): void {
    this.setField('produceStatus', producingStatus);
    this.updateMode();
  }

  setPaymentInfo(info: FormStateSnapshot<DrawDetailsPaymentInfo>): void {
    const originalInfo = getFormStateSnapshotValue(this.value.paymentInfo);
    const modifiedInfo = getFormStateSnapshotValue(info);
    const isStageModified = differentFieldsWith(originalInfo, modifiedInfo).includes('drawRuleStage');
    this.setFieldWithDirty('paymentInfo', info);

    if (isStageModified) {
      this.setValue(selectDrawRuleStage(this.value, modifiedInfo.drawRuleStage));
    }
  }

  setPaymentProgressInfo(info: FormStateSnapshot<DrawDetailsPaymentProgressInfo>): void {
    // fields do not needed recalculate
    const ignoredFields = ['memo'];
    this.setFieldWithDirty('paymentProgressInfo', info);
    const changedProperty: string[] = [];
    for (const key in info.controlState) {
      if (info.controlState.hasOwnProperty(key) && info.controlState[key].dirty) {
        changedProperty.push(key);
      }
    }
    if (IS_REGION_AU_OR_NZ && !!changedProperty.find((item) => !ignoredFields.includes(item))) {
      this.setRecalculationNeeded(true);
    }
  }

  setAmountWithGst(response: DrawCalculateResponse): void {
    this.setValueWithDirty(mergeDrawDetailsAmountWithGst(this.value, response));
    this.setRecalculationNeeded(false);
  }

  setLendersInfo(info: FormStateSnapshot<DrawDetailsLendersInfo>): void {
    this.setField('lendersInfo', info);
  }

  setLendersItemThisDraw(itemId: ID, thisDraw?: number): void {
    this.setFieldWithDirty(
      'lendersSchedule',
      drawLendersScheduleItemArr.update(
        this.value.lendersSchedule,
        itemId,
        (item) => setDrawLendersScheduleItemField(item, 'thisDraw', thisDraw)));
  }

  selectPaymentTab(tab: DrawDetailsPaymentTab): void {
    const { tabs } = this.value;
    this.setField('selectedTab', tab);
    this.setField('tabs', setEntityFields(tabs, {
      isChangeOrdersVisited: tabs.isChangeOrdersVisited || tab === DrawDetailsPaymentTab.ChangeOrders,
      isProgressVisited: tabs.isProgressVisited || tab === DrawDetailsPaymentTab.Progress,
    }));
  }

  selectChangeOrders(selectionModel: SelectionModel): void {
    this.setFieldWithDirty('selectedChangeOrders', selectionModel);
  }

  highlightPaymentInfo(): void {
    this.setField('paymentInfo', markFormStateAsTouched(this.value.paymentInfo));
  }

  highlightPaymentProgressInfo(): void {
    this.setField('paymentProgressInfo', markFormStateAsTouched(this.value.paymentProgressInfo));
  }

  highlightLendersInfo(): void {
    this.setField('lendersInfo', markFormStateAsTouched(this.value.lendersInfo));
  }

  navigateToInvalidLenderScheduleCell(): void {
    const invalidItem = find<DrawDetailsLendersScheduleItem>(this.value.lendersSchedule, isEntityInvalid);

    if (invalidItem) {
      this.setField('lendersScheduleFocusedCell', { id: getDrawLendersScheduleItemId(invalidItem), field: 'thisDraw' });
    }
  }

  setRecalculationNeeded(isRecalculationNeeded: boolean): void {
    this.setField('isRecalculationNeeded', isRecalculationNeeded);
  }

  resetLendersScheduleFocusedCell(): void {
    this.setField('lendersScheduleFocusedCell', void 0);
  }

  setIsPreview(isPreview: boolean): void {
    this.setField('isPreview', isPreview);
  }

  private updateMode(): void {
    if (this.isCreateMode) {
      this.setMode(isProduceStatusInProgress(this.project(queryProduceStatus)) ? FormMode.ViewMode : FormMode.EditMode);
    } else {
      this.setMode(FormMode.ViewMode);
    }
  }
}
